<template>
    <AppModal
        ref="modal"
        :title="$t('labels.assign_values_based_on_product_information')"
        :size="!substitutionFinished ? 'md' : 'full'"
        @on-hide="hide"
    >
        <div class="modal-body pb-2">
            <template v-if="fetchTask.state.isRunning">
                <TaskProgressBar :done="fetchTask.state.done" :total="fetchTask.state.total" :expected-steps="20"/>
            </template>
            <template v-else-if="tokenBalance !== null && tokenBalance <= 0 && !substitutionTask.state.isRunning && !substitutionFinished">
                <Alert type="warning">
                    {{ $t('alerts.insufficient_ai_balance') }}
                </Alert>
            </template>
            <template v-else-if="!substitutionTask.state.isRunning && !substitutionFinished">
                <div class="alert alert-info border-0 alert-dismissible">
                    <button type="button" class="close" data-dismiss="alert"><span>×</span></button>
                    <p>{{ $t('alerts.ai_substitution_description') }}</p>
                    <p>{{ $t('alerts.ai_substitution_warning') }}</p>
                </div>
                <div class="form-group mb-3">
                    <FormGroup :label="$t('labels.detailed_attribute_analysis')">
                        <AppSelect
                            @update:model-value="detailedAttributeAnalysis = $event"
                            :model-value="detailedAttributeAnalysis.value"
                            :selected-value="detailedAttributeAnalysis.label"
                            :options="detailedAttributeAnalysisOptions"
                            option-key="value"
                            option-value="label"
                        />
                    </FormGroup>
                </div>
                <div class="table-responsive">
                    <table class="table table-striped table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th width="60px"><AppCheckbox :model-value="allSelected" @update:model-value="selectAllFilters" /></th>
                                <th width="200px">{{ $t('labels.filter') }}</th>
                                <th width="200px">{{ $t('labels.filter_item') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="filter in currentCategoryFilters">
                                <td>
                                    <AppCheckbox
                                        :model-value="selectedFiltersForSubstitution[filter.uuid]"
                                        @update:model-value="selectFilterForSubstitution(filter)"
                                    />
                                </td>
                                <td>{{ getFilterName(filter) }}</td>
                                <td>
                                    <u v-if="filterItemsByFilterUuid[filter.uuid] === undefined" class="text-muted">{{ $t('labels.empty') }}</u>
                                    <span v-else>{{ filterItemsByFilterUuid[filter.uuid].map(item => item.name).join(', ') }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template v-else-if="substitutionTask.state.isRunning">
                <TaskProgressBar :done="substitutionTask.state.done" :total="substitutionTask.state.total" :expected-steps="50"/>
            </template>
            <template v-else-if="substitutionFinished && !updateTask.state.isRunning">
                <Alert type="warning" v-if="balanceExhaustedDuringProcessing">
                    {{ $t('alerts.ai_balance_exhausted_during_processing') }}
                </Alert>
                <Alert type="warning" v-if="messageFromMarketPlaceError">
                    {{ messageFromMarketPlaceError }}
                </Alert>
                <div class="table-responsive">
                    <table class="table table-bordered datatable-basic dataTable">
                        <thead>
                            <tr>
                                <th width="150px" :class="getOrderingClass('sku')" @click="setOrdering('sku')">{{ $t('table.titles.sku') }}</th>
                                <th width="80px">
                                    <div class="text-truncate">{{ $t('table.titles.image') }}</div>
                                </th>
                                <th width="300px" :class="getOrderingClass('name')" @click="setOrdering('name')">{{ $t('table.titles.name') + ` (${ $t(`shortLang.uk`) })` }}</th>
                                <th v-for="filter in selectedFiltersSortedByName" width="400px">
                                    {{ getFilterName(filter) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="product in sortedProducts">
                                <td>
                                    <AppNullableCell>
                                        <template #default v-if="product.sku">
                                            <router-link class="d-block long-string" :to="{name: 'Product', params: { integrationProductUuid: product.uuid }}">
                                                {{ product.sku }}
                                            </router-link>
                                        </template>
                                    </AppNullableCell>
                                </td>
                                <td>
                                    <img
                                        class="table-image"
                                        :src="product.images[0] ?? PLACEHOLDER_IMAGE_URL"
                                    />
                                </td>
                                <td>
                                    <AppNullableCell>
                                        <template #default v-if="product.names['uk']">
                                            <span class="long-string" :title="product.names['uk']">{{ product.names['uk'] }}</span>
                                        </template>
                                    </AppNullableCell>
                                </td>
                                <td v-for="filter in selectedFiltersSortedByName">
                                    <InputGroup v-if="!comparedFiltersByProductUuid[product.uuid]?.[filter.uuid]?.createMode">
                                        <CatalogAttributeSelect
                                            :attribute="toAttributeFormat(filter)"
                                            :integration="integrationForSelect"
                                            :category="integrationCategory"
                                            :model-value="getMultiSelectValue(product.uuid, filter.uuid)"
                                            @update:model-value="updateMultiSelectValue(product.uuid, filter.uuid, $event)"
                                        />
                                        <template #append>
                                            <div class="d-flex flex-wrap" :style="{gap: '0.5rem'}">
                                                <AppButton
                                                    color="teal"
                                                    @click="enableCreateMode(product.uuid, filter.uuid)"
                                                >
                                                    <i class="icon-plus3"></i>
                                                </AppButton>
                                                <AppButton
                                                    color="indigo"
                                                    @click="regenerateAttributeValue(product, filter)"
                                                    :disabled="isRegeneratingAttribute(product.uuid, filter.uuid)"
                                                >
                                                    <i class="icon-reset" v-if="!isRegeneratingAttribute(product.uuid, filter.uuid)"></i>
                                                    <i class="icon-spinner2 spinner" v-else></i>
                                                </AppButton>
                                            </div>
                                        </template>
                                    </InputGroup>
                                    <InputGroup v-else>
                                        <Input
                                            :model-value="comparedFiltersByProductUuid[product.uuid]?.[filter.uuid]?.values?.[0]"
                                            @update:model-value="(value) => {
                                                comparedFiltersByProductUuid[product.uuid][filter.uuid].values = value ? [value] : []
                                            }"
                                        />
                                        <template #append>
                                            <div class="d-flex flex-wrap" style="gap: 0.5rem">
                                                <AppButton
                                                    color="teal"
                                                    :disabled="!selectedFiltersForSubstitution[filter.uuid].allowCustomValues"
                                                    @click="createFilterItem(comparedFiltersByProductUuid[product.uuid][filter.uuid].values?.[0], filter.uuid, product.uuid)"
                                                >
                                                    <i class="icon-checkmark3"></i>
                                                </AppButton>
                                                <AppButton
                                                    color="danger"
                                                    @click="disableCreateMode(product.uuid, filter.uuid)"
                                                >
                                                    <i class="icon-cross2"></i>
                                                </AppButton>
                                            </div>
                                        </template>
                                    </InputGroup>
                                    <span class="text-muted text-nowrap">
                                        {{ $t('labels.current_value') }}: {{ product.filters.filter(f => f.filterUuid === filter.uuid).map(f => f.filterItemName).join(', ') || '-' }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template v-else>
                <TaskProgressBar :done="updateTask.state.done" :total="updateTask.state.total" :expected-steps="50"/>
            </template>
        </div>
        <div class="modal-footer" v-if="!fetchTask.state.isRunning && !substitutionTask.state.isRunning && !updateTask.state.isRunning">
            <AppButton color="indigo" :disabled="Object.keys(selectedFiltersForSubstitution).length === 0 || (tokenBalance !== null && tokenBalance <= 0)" @click="startSubstitutionFilterItem" v-if="!substitutionFinished">
                {{ $t('buttons.assign') }}
            </AppButton>
            <AppButton color="indigo" @click="updateProductsWithNewAttributes" v-else>
                {{ $t('buttons.update') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import AppCheckbox from '../../../../../js/components/new/form/checkbox/AppCheckbox.vue';
import TaskProgressBar from '../../../../components/new/progress/TaskProgressBar.vue';
import CatalogAttributeSelect from '../sidebarCards/products/edit/CatalogAttributeSelect.vue';
import Input from '../../../../components/new/form/Input.vue';
import InputGroup from '../../../../components/new/form/InputGroup.vue';
import AppNullableCell from '../../../components/tables/AppNullableCell.vue';
import FormGroup from '../../../../components/new/form/group/FormGroup.vue';
import AppSelect from '../../../../components/new/form/select/AppSelect.vue';
import Alert from '../../../../components/AlertComponent.vue';

import HttpRequest from '../../../../services/Http/HttpRequest';
import HttpSender from '../../../../services/Http/HttpSender';
import { httpClient } from '../../../../services/HttpClient';
import { chunk, getMessageFromMarketPlaceError } from '../../../../utils';
import GeminiService from '../../../../services/GeminiService/GeminiService';
import { NotValidJsonStringError } from '../../../../services/Errors/NotValidJsonStringError';
import { TooManyRequestsError } from '../../../../services/Errors/TooManyRequestsError';
import { ServiceUnavailableError } from '../../../../services/Errors/ServiceUnavailableError';
import { createProgressTask } from '../../../../services/Progress/ProgressTask';

export default {
    name: "AssignValuesBasedOnProductInformation",
    components: {
        AppModal, AppButton, AppCheckbox, TaskProgressBar, CatalogAttributeSelect, Input,
        InputGroup, AppNullableCell, FormGroup, AppSelect, Alert
    },
    props: {
        productFilters: {
            type: Object,
            default: () => ({})
        },
        mainIntegrationLanguage: {
            type: String,
            default: 'en'
        },
        integrationCategory: {
            type: Object,
            default: null
        },
        getFilterName: {
            type: Function,
            required: true
        },
        integrationService: {
            type: String,
            required: true
        },
        productsListUrl: {
            type: String,
            default: null
        }
    },
    emits: ['updated:filter-items'],
    data() {
        return {
            productUuids: [],
            totalProductsCount: 0,
            tableSearch: '',
            tableOrders: [],
            products: [],
            currentCategoryFilters: {},
            selectedFiltersForSubstitution: {},

            fetchTask: createProgressTask(),
            substitutionTask: createProgressTask(),
            updateTask: createProgressTask(),
            substitutionFinished: false,

            comparedFiltersByProductUuid: {},

            regeneratingAttributes: {},
            ordering: {},
            filterItemsByFilterUuid: null,
            tokenBalance: null,
            balanceExhaustedDuringProcessing: false,
            messageFromMarketPlaceError: null,
            detailedAttributeAnalysis: {
                value: 'simple',
                label: this.$t('labels.simple')
            },
            detailedAttributeAnalysisOptions: [
                { value: 'simple', label: this.$t('labels.simple') },
                { value: 'depth', label: this.$t('labels.depth') }
            ],
        };
    },
    methods: {
        toAttributeFormat(filter) {
            return {
                ...filter,
                is_multiple: filter.isMultiple,
                source_id: filter.sourceId,
                allow_custom_values: filter.allowCustomValues,
            };
        },
        getOrderingClass(column) {
            if (this.ordering[column] === 'asc') {
                return 'sorting_asc';
            } else if (this.ordering[column] === 'desc') {
                return 'sorting_desc';
            }
            return 'sorting';
        },
        setOrdering(column) {
            if (this.ordering.hasOwnProperty(column)) {
                if (this.ordering[column] === 'asc') {
                    this.ordering[column] = 'desc';
                } else {
                    delete this.ordering[column];
                }
            } else {
                this.ordering[column] = 'asc';
            }
        },
        getNameString(names) {
            return names[this.mainIntegrationLanguage] ?? Object.values(names)[0] ?? null;
        },
        async show(productUuids, totalProductsCount = 0, search = '', orders = []) {
            this.totalProductsCount = totalProductsCount ?? 0;
            this.tableSearch = search ?? '';
            this.tableOrders = orders ?? [];
            await this.fetchBalance();
            this.$refs.modal.show();

            await this.fetchTask.run(async (progress) => {
                progress.tick();

                await this.fetchFiltersItems(progress);

                let selectedProductUuids = productUuids;

                if (selectedProductUuids.length === 0) {
                    selectedProductUuids = await this.fetchProductUuidsByTableFilters(progress);
                }

                if (progress.signal.aborted) {
                    return;
                }

                this.productUuids = selectedProductUuids;
                this.products = await this.fetchProducts(progress);
            });
        },
        hide() {
            this.fetchTask.cancel();
            this.substitutionTask.cancel();
            this.updateTask.cancel();
            this.$refs.modal.hide();
            this.productUuids = [];
            this.totalProductsCount = 0;
            this.tableSearch = '';
            this.tableOrders = [];
            this.products = [];
            this.currentCategoryFilters = {};
            this.selectedFiltersForSubstitution = {};
            this.substitutionFinished = false;
            this.comparedFiltersByProductUuid = {};
            this.regeneratingAttributes = {};
            this.filterItemsByFilterUuid = null;
            this.tokenBalance = null;
            this.balanceExhaustedDuringProcessing = false;
            this.messageFromMarketPlaceError = null;
        },
        async fetchFiltersItems(progress) {
            let filtersWithItems = await this.fetchAllFilters(progress);

            this.currentCategoryFilters = this.sortFiltersByName(filtersWithItems);
        },
        sortFiltersByName(filters) {
            return Object.fromEntries(
                Object.entries(filters).sort(([, a], [, b]) => a.name.localeCompare(b.name))
            );
        },
        async fetchAllFilters(progress) {
            const totalFilters = await this.getFiltersCount();
            if (totalFilters === 0) return [[], {}];

            const filterRequests = this.createFilterRequests(totalFilters);
            return await this.executeFilterRequests(filterRequests, progress);
        },
        async getFiltersCount() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                integration_category_uuids: [this.integrationCategory.uuid],
                limit: 1,
                has_default_items: 0,
            });
            return response.data.meta.total;
        },
        createFilterRequests(totalFilters) {
            const requests = [];
            const pageSize = 10;

            for (let i = 0; i < totalFilters; i += pageSize) {
                requests.push(
                    new HttpRequest('GET', `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                        limit: pageSize,
                        integration_category_uuids: [this.integrationCategory.uuid],
                        page: Math.floor(i / pageSize) + 1,
                        has_default_items: 0,
                    })
                );
            }

            return requests;
        },
        async executeFilterRequests(requests, {tick}) {
            const filtersWithItems = {};

            await new HttpSender().sendRequests(requests, (request, response) => {
                tick();
                if (response.body.status) return;

                for (let filter of response.body.data) {
                    filtersWithItems[filter.uuid] = this.createFilterStructure(filter);
                }
            });

            return filtersWithItems;
        },
        async fetchAllFilterItems(progress) {
            if (Object.keys(this.selectedFiltersForSubstitution).length === 0) return {};

            let filterItemRequests = [];

            for (let [filterUuid, filter] of Object.entries(this.selectedFiltersForSubstitution)) {
                let filterItemsCount = 0;
                let marketplaceFilterItemsCount = 0;

                await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                    integration_filter_uuids: [filterUuid],
                    limit: 1
                }).then(response => {
                    filterItemsCount = response.data.meta.total;
                });

                if (filterItemsCount > 0) {
                    for (let i = 0; i < filterItemsCount; i += 10) {
                        filterItemRequests.push(
                            new HttpRequest('GET', `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                                limit: 10,
                                page: Math.floor(i / 10) + 1,
                                integration_filter_uuids: [filterUuid]
                            })
                        );
                    }
                };

                if (this.integrationCategory.source_id === null || filter.sourceId === null) continue;

                // TODO: temporary, remove the flag once the global 404 redirect only happens on page navigation
                await httpClient.get(`/projects/${this.$route.params.projectUuid}/marketplace/sources/${this.integrationService}/categories/${this.integrationCategory.source_id}/attributes/${filter.sourceId}/items`, {
                    limit: 1
                }, false, undefined, { skipNotFoundRedirect: true }).then(response => {
                    marketplaceFilterItemsCount = response.data.meta.total;
                }).catch(error => {
                    const messageFromMarketPlaceError = getMessageFromMarketPlaceError(error);
                    if (messageFromMarketPlaceError) {
                        this.messageFromMarketPlaceError = messageFromMarketPlaceError;
                        return;
                    }
                    throw error;
                });

                if (marketplaceFilterItemsCount > 0) {
                    for (let i = 0; i < marketplaceFilterItemsCount; i += 10) {
                        const req = new HttpRequest('GET', `/projects/${this.$route.params.projectUuid}/marketplace/sources/${this.integrationService}/categories/${this.integrationCategory.source_id}/attributes/${filter.sourceId}/items`, {
                            limit: 10,
                            page: Math.floor(i / 10) + 1
                        });
                        req.integrationFilterUuid = filterUuid;
                        filterItemRequests.push(req);
                    }
                }
            }

            return await this.executeFilterItemRequests(filterItemRequests, this.selectedFiltersForSubstitution, progress);
        },
        async getFilterItemsCount(filterUuids) {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                integration_filter_uuids: filterUuids,
                limit: 1
            });

            return response.data.meta.total;
        },
        createFilterItemRequests(filterUuids, totalFilterItems) {
            const requests = [];
            const pageSize = 50;

            for (let i = 0; i < totalFilterItems; i += pageSize) {
                requests.push(
                    new HttpRequest('GET', `/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, {
                        limit: pageSize,
                        page: Math.floor(i / pageSize) + 1,
                        integration_filter_uuids: filterUuids
                    })
                );
            }

            return requests;
        },
        async executeFilterItemRequests(requests, filtersWithItems, {tick}) {
            await new HttpSender().sendRequests(requests, (request, response) => {
                tick();
                if (response.body.status) return;

                let requestData = request.body || {};

                let requestFilterUuid = request.integrationFilterUuid;

                if (!requestFilterUuid && requestData.integration_filter_uuids) {
                    requestFilterUuid = requestData.integration_filter_uuids[0];
                }

                this.processFilterItems(response.body.data, filtersWithItems, requestFilterUuid);
            });

            return filtersWithItems;
        },
        processFilterItems(items, filtersWithItems, requestFilterUuid) {
            items.forEach(item => {
                const filterUuid = item.integration_filter_uuid || requestFilterUuid;
                
                if (!filterUuid || !filtersWithItems[filterUuid]) return;

                const isMarketplace = item.integration_filter_uuid === undefined;

                let itemName = item.name;
                
                if (!itemName) {
                    const names = item.names || Object.entries(item).reduce((acc, [key, val]) => {
                        if (key.startsWith('name_')) acc[key.replace('name_', '')] = val;
                        return acc;
                    }, {});
                    
                    if (Object.keys(names).length) {
                        itemName = this.getNameString(names);
                    }
                }

                if (!itemName) return;

                const filterItems = filtersWithItems[filterUuid].filterItems;
                const existingItem = filterItems.find(existingItem => existingItem.name === itemName);
                
                if (!existingItem) {
                    filterItems.push({
                        uuid: item.uuid,
                        name: itemName,
                        isMarketplace,
                        marketplaceSourceId: isMarketplace ? item.source_id : null
                    });
                } else if (!isMarketplace && existingItem.isMarketplace) {
                    existingItem.isMarketplace = false;
                    existingItem.uuid = item.uuid;
                }
            });
        },
        createFilterStructure(item) {
            return {
                uuid: item.uuid,
                name: item.name,
                type: item.type,
                filterItems: [],
                allowCustomValues: item.allow_custom_values,
                measurement_unit: item.measurement_unit,
                isMultiple: item.is_multiple,
                sourceId: item.source_id
            };
        },
        async fetchBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        },
        buildTableListParams() {
            let params = {...this.productFilters};

            if (this.tableSearch) {
                params.search = this.tableSearch;
            }

            if (this.tableOrders.length > 0) {
                params.orders = this.tableOrders;
            }

            return params;
        },
        async fetchProductUuidsByTableFilters(progress) {
            if (!this.productsListUrl) {
                return [];
            }

            const listParams = this.buildTableListParams();
            const pageSize = 50;

            let totalProducts = this.totalProductsCount;

            if (!totalProducts) {
                const response = await httpClient.get(this.productsListUrl, {...listParams, limit: 1});
                totalProducts = response.data.meta.total;
            }

            if (!totalProducts) {
                return [];
            }

            let paramsByPage = [];

            for (let page = 1; page <= Math.ceil(totalProducts / pageSize); page++) {
                paramsByPage.push({...listParams, limit: pageSize, page: page});
            }

            const responses = await this.fetchPages(this.productsListUrl, paramsByPage, progress);
            const uniqueProductUuids = new Set();

            responses.forEach(response => {
                response.data.data.forEach(product => uniqueProductUuids.add(product.uuid));
            });

            const productUuids = Array.from(uniqueProductUuids);

            return productUuids;
        },
        async fetchPages(url, paramsByPage, {signal, tick}) {
            const concurrency = 10;
            const responses = [];

            for (let i = 0; i < paramsByPage.length; i += concurrency) {
                if (signal.aborted) {
                    break;
                }

                const chunkedParams = paramsByPage.slice(i, i + concurrency);
                const pendingResponses = chunkedParams.map(params => httpClient.get(url, params));
                const chunkedResponses = await Promise.all(pendingResponses);

                chunkedResponses.forEach(response => {
                    tick();
                    responses.push(response);
                });
            }

            return responses;
        },
        collectProductRequestParams() {
            let paramsByPage = [];

            for (let chunkedUuids of chunk(this.productUuids, 10)) {
                paramsByPage.push({
                    uuids: chunkedUuids,
                    limit: chunkedUuids.length,
                    category_uuids: [this.integrationCategory.uuid],
                    extra_fields: ['integrationFilterItems']
                });
            }

            return paramsByPage;
        },
        async fetchProducts(progress) {
            let products = [],
                filterItemsUsageStats = {},
                filterItemsByUuid = {},
                filterItemsByFilterUuid = {};

            const productRequestParams = this.collectProductRequestParams();
            const responses = await this.fetchPages(`/integrations/${this.$route.params.integrationUuid}/products`, productRequestParams, progress);

            responses.forEach(response => {
                if (progress.signal.aborted) return;

                response.data.data.forEach(product => {
                    for (const filterItem of product.integration_filter_items) {
                        const filterUuid = filterItem.integration_filter_uuid;
                        const filterItemUuid = filterItem.uuid;

                        filterItemsByUuid[filterItemUuid] = filterItem;

                        if (!filterItemsUsageStats[filterUuid]) {
                            filterItemsUsageStats[filterUuid] = {};
                        }
                        if (!filterItemsUsageStats[filterUuid][filterItemUuid]) {
                            filterItemsUsageStats[filterUuid][filterItemUuid] = 0;
                        }
                        filterItemsUsageStats[filterUuid][filterItemUuid]++;
                    }
                    products.push(
                        this.extractProductData(product)
                    );
                });
            })

            const totalProducts = products.length;

            for (const [filterUuid, itemsMap] of Object.entries(filterItemsUsageStats)) {
                const itemsUsedInAllProducts = Object.entries(itemsMap)
                    .filter(([, count]) => count === totalProducts)
                    .map(([filterItemUuid]) => filterItemsByUuid[filterItemUuid]);

                if (itemsUsedInAllProducts.length !== Object.entries(itemsMap).length) {
                    continue;
                }

                if (itemsUsedInAllProducts.length > 0) {
                    filterItemsByFilterUuid[filterUuid] = itemsUsedInAllProducts;
                }
            }

            this.filterItemsByFilterUuid = filterItemsByFilterUuid;

            return products;
        },
        extractProductData(productData) {
            return {
                uuid: productData.uuid,
                names: productData.names,
                descriptions: productData.descriptions,
                filters: productData.integration_filter_items.reduce((acc, item) => {
                    acc.push({
                        filterUuid: item.integration_filter_uuid,
                        filterItemUuid: item.uuid,
                        filterName: item.integration_filter.name,
                        filterItemName: item.name,
                        isMultiple: item.integration_filter.is_multiple
                    })
                    return acc;
                }, []),
                images: productData.images,
                sku: productData.sku,
            };
        },
        selectFilterItem(productUuid, filterUuid, filterItem) {
            this.ensureFilterStructureExists(productUuid, filterUuid);

            if (filterItem) {
                this.comparedFiltersByProductUuid[productUuid][filterUuid].values = [filterItem.uuid];
            } else {
                this.comparedFiltersByProductUuid[productUuid][filterUuid].values = [];
            }
        },
        ensureFilterStructureExists(productUuid, filterUuid) {
            if (!this.comparedFiltersByProductUuid[productUuid]) {
                this.comparedFiltersByProductUuid[productUuid] = {};
            }

            if (!this.comparedFiltersByProductUuid[productUuid][filterUuid]) {
                this.comparedFiltersByProductUuid[productUuid][filterUuid] = {
                    values: [],
                    createMode: false
                };
            }
        },
        async processProductsWithAI(products, geminiService, progress) {
            geminiService = geminiService ?? new GeminiService();
            let clonedProducts = JSON.parse(JSON.stringify(products));

            let filtersForPrompt;
            let searchedFiltersByKeywords = {};

            if (this.detailedAttributeAnalysis.value === 'depth') {
                filtersForPrompt = await this.prepareFiltersForPrompt(
                    Object.values(this.selectedFiltersForSubstitution),
                    geminiService
                );

                const productsForPrompt = clonedProducts.map(function (product) {
                    product.filters = product.filters.filter(filter => !this.selectedFiltersForSubstitution[filter.filterUuid]);

                    return product;
                }, this);

                searchedFiltersByKeywords = await this.searchFiltersByKeywords(
                    productsForPrompt,
                    filtersForPrompt,
                    geminiService
                );
            } else {
                filtersForPrompt = Object.values(this.selectedFiltersForSubstitution).map(filter => ({ uuid: filter.uuid, name: filter.name }));
                searchedFiltersByKeywords = {};
            }

            let productsWithSubstitutedFilterItems = searchedFiltersByKeywords;

            const filtersForSearchingInList = filtersForPrompt.map(function (filter) {
                filter.filterItems = this.selectedFiltersForSubstitution[filter.uuid].filterItems;

                return filter;
            }, this).filter(filter => filter.filterItems.length <= 500);

            if (!filtersForSearchingInList.length) {
                return searchedFiltersByKeywords;
            }

            const productsForSearchingInList = products.filter(function (product) {
                if (!searchedFiltersByKeywords[product.uuid]) {
                    return true;
                }

                const previousSelectedFilterUuids = Object.keys(searchedFiltersByKeywords[product.uuid]);

                const notSelectedFilter = filtersForSearchingInList.filter(filter => !previousSelectedFilterUuids.includes(filter.uuid));

                return notSelectedFilter.length > 0;
            });

            if (!productsForSearchingInList.length) {
                return searchedFiltersByKeywords;
            }

            const chunkedProducts = chunk(productsForSearchingInList, 10);

            for (let chunkedProduct of chunkedProducts) {
                if (progress.signal.aborted) break;

                const result = await this.processProductChunk(chunkedProduct, filtersForSearchingInList, geminiService, progress);

                if (result) {
                    for (const [productUuid, filters] of Object.entries(result)) {
                        if (!Object.hasOwn(productsWithSubstitutedFilterItems, productUuid)) {
                            productsWithSubstitutedFilterItems[productUuid] = {};
                        }

                        for (const [filterUuid, value] of Object.entries(filters)) {
                            if (Object.hasOwn(productsWithSubstitutedFilterItems[productUuid], filterUuid) || !(typeof value === 'string' || typeof value === 'number')) {
                                continue;
                            }

                            productsWithSubstitutedFilterItems[productUuid][filterUuid] = String(value);
                        }
                    }
                }

                await geminiService.flushTokens(this.$route.params.projectUuid, 'Підстановка атрибутів (масова дія)');
                await this.fetchBalance();
                if (this.tokenBalance !== null && this.tokenBalance <= 0) {
                    this.balanceExhaustedDuringProcessing = true;
                    break;
                }
            }

            return productsWithSubstitutedFilterItems;
        },
        getUniqueKeyWordsFromString(string)
        {
            const keyWords = string
                .toLowerCase()
                .split(/[^\p{L}\p{N}]+/u)
                .filter(Boolean);

            return new Set(keyWords);
        },
        async prepareFiltersForPrompt(filters, geminiService) {
            let filtersForPrompt = filters.map(function (filter) {
                return {
                    uuid: filter.uuid,
                    name: filter.name,
                }
            })

            let attributeDefinitionsPrompt = `
                You are given:
                - A product category name.
                - A list of attributes; each attribute has: uuid and name.

                Goal:
                For each attribute, write a short, clear, unambiguous definition tailored to this category.

                Rules:
                1) Disambiguate the intended meaning (e.g., “Compatible brand” = brand of the device the accessory fits, not the accessory manufacturer).
                2) The definition should be concise (1–2 sentences), actionable for information extraction.
                3) Use neutral, domain-appropriate language.
                4) Produce a definition for every input attribute.
                5) Return STRICTLY valid JSON. JSON only, no extra text.

                Input:
                Category names: ${JSON.stringify(this.integrationCategory.names)}
                Attributes: ${JSON.stringify(filtersForPrompt)}

                Expected output (STRICT JSON object):
                {
                  "<attributeUuid>": "Attribute definition string",
                  "<attributeUuid>": "Attribute definition string"
                }
            `;

            let attributeDefinitions = {};

            try {
                attributeDefinitions = await geminiService.generateWithRetry(attributeDefinitionsPrompt);
            } catch (error) {
                return filtersForPrompt;
            }

            return filtersForPrompt.map(function (filter) {
                return {
                    uuid: filter.uuid,
                    name: filter.name,
                    definition : attributeDefinitions[filter.uuid] ?? null,
                }
            })
        },
        async searchFiltersByKeywords(productsForPrompt, filtersForPrompt, geminiService)
        {
            const productsWithSubstitutedFilterItems = {};

            for (let chunkedProducts of chunk(productsForPrompt, 10))
            {
                let prompt = `
                    You are given:
                    1. An array of product objects. Each product contains: uuid, name, description, keywords (possibly multilingual), and images.
                    2. An array of attribute definitions, each containing: uuid, name, and an optional "definition" string.

                    Your task is to determine the most appropriate value for each attribute of each product based on:
                    - Semantic understanding of the product’s textual data (names, descriptions, keywords, in all available languages).
                    - Visual analysis of the product’s images (e.g., color, pattern, material, style, category).

                    Rules:
                    1. Iterate through all products in the array.
                    2. Use only the provided list of attributes (uuid + name + optional definition).
                    3. Process each product independently; reset context between products. Do not copy or infer values for one product from any other product in the batch.
                    4. Treat each product–attribute pair independently, as if you were given only that single product and that single attribute. Do not use other attributes of the same product to infer values; do not propagate or reuse values across attributes; do not rely on category-wide defaults.
                    5. Within a product, reset context between attributes. A decision for one attribute must not influence any other attribute for that product.
                    6. If an attribute object contains a non-null "definition", treat it as authoritative and follow it strictly when determining that attribute’s value. If any cue conflicts with the definition, the definition takes precedence.
                    7. If you can confidently determine an attribute value, return it as a human-readable string (e.g., "Green", "Cotton", "Striped", "42").
                    8. If you cannot determine the value with high confidence, do not include that attribute in the output.
                    9. Do not fabricate or generalize values.
                    10. The response must be STRICTLY valid JSON.
                    11. For each product, the top-level key must be its uuid.
                    12. Nested keys must be attribute uuids.
                    13. Nested values must be attribute-item uuids.
                    14. Values must be strings containing the most likely attribute value.
                    15. No explanations, no additional text — JSON only.

                    Input format:
                    Products: ${JSON.stringify(chunkedProducts)}
                    Attributes: ${JSON.stringify(filtersForPrompt)}

                    Expected output format:
                    {
                      "productUuid1": {
                        "attributeUuid1": "AttributeItemUuid1",
                        "attributeUuid2": "AttributeItemUuid2"
                      },
                      "productUuid2": {
                        "attributeUuid1": "AttributeItemUuid1"
                      }
                    }
                `;

                let productFilterTextValues = {};

                try {
                    productFilterTextValues = await geminiService.generateWithRetry(prompt);
                } catch (error) {
                    continue;
                }

                for (const [productUuid, attributes] of Object.entries(productFilterTextValues)) {
                    for (const [attributeUuid, textValue] of Object.entries(attributes)) {
                        if (textValue === null) {
                            continue;
                        }

                        const searchKeyWords = this.getUniqueKeyWordsFromString(textValue);

                        const filterItems = this.selectedFiltersForSubstitution[attributeUuid].filterItems;

                        const matchesFilterItems = filterItems
                            .map(({ name, uuid }) => {
                                const itemKeyWords = Array.from(new Set(this.getUniqueKeyWordsFromString(name)));

                                const numberOfMatches = itemKeyWords.reduce(
                                    (acc, t) => acc + (searchKeyWords.has(t) ? 1 : 0),
                                    0
                                );

                                return { name, uuid, numberOfMatches, keyWordsCount: itemKeyWords.length };
                            })
                            .filter(item => item.numberOfMatches > 0)
                            .sort((a, b) =>
                                (b.numberOfMatches - a.numberOfMatches) ||
                                (a.keyWordsCount - b.keyWordsCount) ||
                                a.name.localeCompare(b.name)
                            );

                        if (matchesFilterItems.length === 0) {
                            continue;
                        }

                        const topMatchesFilterItem = matchesFilterItems[0];

                        if (topMatchesFilterItem.keyWordsCount === topMatchesFilterItem.numberOfMatches) {
                            if (!productsWithSubstitutedFilterItems[productUuid]) {
                                productsWithSubstitutedFilterItems[productUuid] = {};
                            }

                            productsWithSubstitutedFilterItems[productUuid][attributeUuid] = topMatchesFilterItem.uuid;
                        }
                    }
                }

                await geminiService.flushTokens(this.$route.params.projectUuid, 'Підстановка атрибутів (масова дія)');
                await this.fetchBalance();
                if (this.tokenBalance !== null && this.tokenBalance <= 0) {
                    this.balanceExhaustedDuringProcessing = true;
                    break;
                }
            }

            return productsWithSubstitutedFilterItems;
        },
        async processProductChunk(productChunk, filters, geminiService, {signal, tick}) {
            try {
                const part = this.createPrompt(productChunk, filters);
                const result = await geminiService.generateWithRetry(part);

                if (signal.aborted) return;

                tick();

                return result;
            } catch (error) {
                return;
            }
        },
        async updateProductsWithNewAttributes() {
            const currentAndSubstitutedItems = this.mergeProductsWithSubstitutedItems(this.products, this.comparedFiltersByProductUuid);
            const updateRequests = this.createProductUpdateRequests(currentAndSubstitutedItems);

            const completed = await this.updateTask.run(async ({setTotal, tick}) => {
                setTotal(updateRequests.length);

                await new HttpSender().sendRequestsInBatchesSuccessively(updateRequests, () => tick());
            });

            if (completed) {
                this.$emit('updated:filter-items');
                this.hide();
            }
        },
        createProductUpdateRequests(productsWithSubstitutedFilterItems) {
            return Object.entries(productsWithSubstitutedFilterItems).map(([productUuid, attributes]) => {
                    const filterItemUuids = Object.values(attributes)
                        .filter(value => !value.createMode && Array.isArray(value.values))
                        .flatMap(value => value.values)
                        .filter(Boolean);

                    return new HttpRequest('PUT', `/integrations/products/${productUuid}`, {
                        filter_item_uuids: filterItemUuids
                    });
                }
            );
        },
        async startSubstitutionFilterItem() {
            const completed = await this.substitutionTask.run(async (progress) => {
                const signal = progress.signal;

                this.currentCategoryFilters = await this.fetchAllFilterItems(progress);

                if (signal.aborted) return;
                const geminiService = new GeminiService();
                const substitutedItems = await this.processProductsWithAI(this.products, geminiService, progress);

                await geminiService.flushTokens(this.$route.params.projectUuid, 'Підстановка атрибутів (масова дія)');

                if (signal.aborted) return;
                this.comparedFiltersByProductUuid = this.formatFiltersData(substitutedItems);

                if (signal.aborted) return;
                let itemsToCreate = [];
                for (const [pUuid, filters] of Object.entries(this.comparedFiltersByProductUuid)) {
                    for (const [fUuid, val] of Object.entries(filters)) {
                        if (val.createMode && val.isMarketplace && Array.isArray(val.values) && val.values.length > 0) {
                            const name = val.values[0];
                            if (!itemsToCreate.some(item => item.filterUuid === fUuid && item.name === name)) {
                                itemsToCreate.push({ filterUuid: fUuid, name, marketplaceSourceId: val.marketplaceSourceId });
                            }
                        }
                    }
                }

                for (let item of itemsToCreate) {
                    if (signal.aborted) return;

                    await this.createFilterItem(item.name, item.filterUuid, null, item.marketplaceSourceId).catch(() => {});
                }
            });

            if (completed) {
                this.substitutionFinished = true;
            }
        },
        formatFiltersData(filtersByProductUuid) {
            let formattedFiltersData = {};

            for (let [productUuid, filterItemUuidByFilterUuid] of Object.entries(filtersByProductUuid)) {
                formattedFiltersData[productUuid] = {};

                for (let [filterUuid, filterItemUuid] of Object.entries(filterItemUuidByFilterUuid)) {
                    const formatResult = this.formatFilterData(filterUuid, filterItemUuid);

                    if (!formatResult) {
                        continue;
                    }

                    formattedFiltersData[productUuid][filterUuid] = formatResult;

                }
            }

            return formattedFiltersData;
        },
        formatFilterData(filterUuid, filterItemUuid) {
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

            if (!filterItemUuid) return null;

            const filter = this.currentCategoryFilters[filterUuid];
            if (!filter) return null;

            const isUuid = uuidRegex.test(filterItemUuid);

            if (!isUuid && !filter.allowCustomValues) return null;

            let foundItem = null;
            if (!isUuid) {
                foundItem = filter.filterItems.find(item => item.name === filterItemUuid);
            } else {
                foundItem = filter.filterItems.find(item => item.uuid === filterItemUuid);
            }

            if (foundItem) {
                if (foundItem.isMarketplace) {
                    return {
                        createMode: true,
                        values: [foundItem.name],
                        isMarketplace: true,
                        marketplaceSourceId: foundItem.marketplaceSourceId
                    };
                }
                return {
                    createMode: false,
                    values: [foundItem.uuid]
                };
            }

            return {
                createMode: !isUuid,
                values: [filterItemUuid]
            };
        },
        mergeProductsWithSubstitutedItems(products, substitutedItems) {
            return products.reduce((acc, product) => {
                acc[product.uuid] = substitutedItems[product.uuid] || {};

                for (const filter of product.filters) {
                    if (!acc[product.uuid][filter.filterUuid]) {
                        acc[product.uuid][filter.filterUuid] = {
                            createMode: false,
                            values: filter.filterItemUuid ? [filter.filterItemUuid] : []
                        };
                    } else if (filter.isMultiple) {
                        const values = acc[product.uuid][filter.filterUuid].values;
                        if (filter.filterItemUuid && !values.includes(filter.filterItemUuid)) {
                            values.push(filter.filterItemUuid);
                        }
                    }
                }

                return acc;
            }, {});
        },
        selectFilterForSubstitution(filter) {
            if (this.selectedFiltersForSubstitution[filter.uuid]) {
                delete this.selectedFiltersForSubstitution[filter.uuid];
            } else {
                this.selectedFiltersForSubstitution[filter.uuid] = filter;
            }
        },
        createPrompt(products, filters) {
            let prompt = `
                You are given a product object and a list of attribute definitions.

                Your task is to analyze the product's data — including multilingual names, descriptions, keywords, and associated images — and determine the most appropriate value for each attribute from the provided options.

                Attribute values should be selected based on:

                semantic understanding of the product name, description, and keywords (in all available languages)

                visual analysis of the product images (e.g., for attributes like color or pattern)

                If you do not find any matches among the existing values, instead of the attributeItemUuid value, you can return a string for attributes of type "string" and a number for attributes of type "number" with the most accurate value you think, note that.

                If you can't determine the value, don't substitute anything.

                Return the result strictly as a valid JSON object, where each key is the attribute name and each value is the selected attribute value from the provided list. Do not return any explanation or extra text.

                The UUID must match exactly what I am sending. 

                Product: ${JSON.stringify(products)}

                Attributes: ${JSON.stringify(filters)}

                Expected output format:

                {
                    'productUuid' : {
                        ‘attributeUuid1’ : 'attributeItemUuid',
                        ‘attributeUuid2’ : 'attributeItemUuid',
                    }
                }
            `
            return prompt;
        },
        selectAllFilters() {
            if (this.allSelected) {
                this.selectedFiltersForSubstitution = {};
            } else {
                Object.values(this.currentCategoryFilters).forEach(filter => {
                    this.selectedFiltersForSubstitution[filter.uuid] = filter;
                });
            }
        },
        findFilterItemName(filterUuid, filterItemUuid) {
            const filter = this.currentCategoryFilters[filterUuid];
            if (!filter) return null;

            const filterItem = filter.filterItems.find(item => item.uuid === filterItemUuid);
            return filterItem ? filterItem.name : null;
        },
        async createFilterItem(filterItemName, filterUuid, productUuid, marketplaceSourceId = null) {
            let requestData = {
                integration_filter_uuid: filterUuid,
                names: {
                    [this.mainIntegrationLanguage]: filterItemName
                }
            };
            
            if (marketplaceSourceId) {
                requestData.source_id = marketplaceSourceId;
            }

            await httpClient.post(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filter-items`, requestData).then(response => {
                for (let [pUuid, filters] of Object.entries(this.comparedFiltersByProductUuid)) {
                    if (filters.hasOwnProperty(filterUuid) && filters[filterUuid].createMode === true && Array.isArray(filters[filterUuid].values) && filters[filterUuid].values[0] === filterItemName) {
                        this.comparedFiltersByProductUuid[pUuid][filterUuid] = {
                            createMode: false,
                            values: [response.data.data.uuid]
                        };
                    }
                }

                this.selectedFiltersForSubstitution[filterUuid].filterItems.push({
                    uuid: response.data.data.uuid,
                    name: filterItemName
                });
            })
        },
        async regenerateAttributeValue(product, filter) {
            this.setRegeneratingAttribute(product.uuid, filter.uuid, true);

            try {
                const geminiService = new GeminiService();

                // Prepare filter for prompt
                const filtersForPrompt = await this.prepareFiltersForPrompt([filter], geminiService);
                if (!filtersForPrompt.length) return;

                // Create a copy of the product without the current value of this filter
                const productForPrompt = {
                    uuid: product.uuid,
                    names: product.names,
                    descriptions: product.descriptions,
                    filters: product.filters.filter(f => f.filterUuid !== filter.uuid),
                    images: product.images,
                    sku: product.sku
                };

                // Forming a prompt for a single product and a single attribute
                const prompt = `
                    You are given:
                    1. A single product object with: uuid, name, description, keywords, and images.
                    2. A single attribute definition with: uuid, name, and an optional "definition" string.

                    Your task is to determine the most appropriate value for this specific attribute of this specific product based on:
                    - Semantic understanding of the product's textual data (names, descriptions, keywords, in all available languages).
                    - Visual analysis of the product's images (e.g., color, pattern, material, style, category).

                    Rules:
                    1. If the attribute contains a non-null "definition", treat it as authoritative and follow it strictly.
                    2. If you can confidently determine the attribute value, return it as a human-readable string.
                    3. If you cannot determine the value with high confidence, return null.
                    4. Do not fabricate or generalize values.
                    5. The response must be STRICTLY valid JSON.
                    6. Return only the attribute value as a string, or null if uncertain.

                    Input:
                    Product: ${JSON.stringify(productForPrompt)}
                    Attribute: ${JSON.stringify(filtersForPrompt[0])}

                    Expected output format (string value or null):
                    "Attribute Value" or null
                `;

                // Getting the response from AI
                const aiResponse = await geminiService.generateWithRetry(prompt);

                if (!aiResponse || aiResponse === null) {
                    // If AI could not determine the value, clear the field
                    this.selectFilterItem(product.uuid, filter.uuid, null);
                    return;
                }

                // Searching for the best match among the existing filter values
                const searchKeyWords = this.getUniqueKeyWordsFromString(aiResponse);
                const filterItems = filter.filterItems;

                const matchesFilterItems = filterItems
                    .map(({ name, uuid }) => {
                        const itemKeyWords = Array.from(new Set(this.getUniqueKeyWordsFromString(name)));
                        const numberOfMatches = itemKeyWords.reduce(
                            (acc, keyword) => acc + (searchKeyWords.has(keyword) ? 1 : 0),
                            0
                        );
                        return { name, uuid, numberOfMatches, keyWordsCount: itemKeyWords.length };
                    })
                    .filter(item => item.numberOfMatches > 0)
                    .sort((a, b) =>
                        (b.numberOfMatches - a.numberOfMatches) ||
                        (a.keyWordsCount - b.keyWordsCount) ||
                        a.name.localeCompare(b.name)
                    );

                if (matchesFilterItems.length > 0) {
                    const bestMatch = matchesFilterItems[0];
                    const originalItem = filter.filterItems.find(item => item.uuid === bestMatch.uuid);
                    // Check if this is an exact match (all keywords match)
                    if (bestMatch.keyWordsCount === bestMatch.numberOfMatches) {
                        if (originalItem && originalItem.isMarketplace) {
                            this.ensureFilterStructureExists(product.uuid, filter.uuid);
                            this.comparedFiltersByProductUuid[product.uuid][filter.uuid].values = [originalItem.name];
                            this.comparedFiltersByProductUuid[product.uuid][filter.uuid].createMode = true;
                            await this.createFilterItem(originalItem.name, filter.uuid, product.uuid, originalItem.marketplaceSourceId).catch(() => {});
                        } else {
                            this.selectFilterItem(product.uuid, filter.uuid, { uuid: bestMatch.uuid });
                        }
                    } else {
                        // Partial match - if custom values are allowed, we can create a new one
                        if (filter.allowCustomValues) {
                            this.ensureFilterStructureExists(product.uuid, filter.uuid);
                            this.comparedFiltersByProductUuid[product.uuid][filter.uuid].values = [aiResponse];
                            this.comparedFiltersByProductUuid[product.uuid][filter.uuid].createMode = true;
                        } else {
                            // Select the best match
                            this.selectFilterItem(product.uuid, filter.uuid, { uuid: bestMatch.uuid });
                        }
                    }
                } else {
                    // No matches found
                    if (filter.allowCustomValues) {
                        this.ensureFilterStructureExists(product.uuid, filter.uuid);
                        this.comparedFiltersByProductUuid[product.uuid][filter.uuid].values = [aiResponse];
                        this.comparedFiltersByProductUuid[product.uuid][filter.uuid].createMode = true;
                    } else {
                        this.selectFilterItem(product.uuid, filter.uuid, null);
                    }
                }
            } finally {
                this.setRegeneratingAttribute(product.uuid, filter.uuid, false);
            }
        },
        enableCreateMode(productUuid, filterUuid) {
            this.ensureFilterStructureExists(productUuid, filterUuid);
            this.comparedFiltersByProductUuid[productUuid][filterUuid].values = [];
            this.comparedFiltersByProductUuid[productUuid][filterUuid].createMode = true;
        },
        getMultiSelectValue(productUuid, filterUuid) {
            const values = this.comparedFiltersByProductUuid[productUuid]?.[filterUuid]?.values ?? [];
            return values.map(uuid => {
                return {
                    uuid: uuid,
                    name: this.findFilterItemName(filterUuid, uuid) || uuid
                };
            });
        },
        updateMultiSelectValue(productUuid, filterUuid, items) {
            this.ensureFilterStructureExists(productUuid, filterUuid);
            this.comparedFiltersByProductUuid[productUuid][filterUuid].values = items.map(item => item.uuid);
            this.currentCategoryFilters[filterUuid].filterItems.push(...items);
        },
        disableCreateMode(productUuid, filterUuid) {
            this.comparedFiltersByProductUuid[productUuid][filterUuid].values = [];
            this.comparedFiltersByProductUuid[productUuid][filterUuid].createMode = false;
        },
        isRegeneratingAttribute(productUuid, filterUuid) {
            const key = `${productUuid}-${filterUuid}`;
            return this.regeneratingAttributes[key] || false;
        },
        setRegeneratingAttribute(productUuid, filterUuid, isLoading) {
            const key = `${productUuid}-${filterUuid}`;
            if (isLoading) {
                this.regeneratingAttributes[key] = true;
            } else {
                delete this.regeneratingAttributes[key];
            }
        }
    },
    computed: {
        integrationForSelect() {
            return {
                uuid: this.$route.params.integrationUuid,
                service: this.integrationService,
            };
        },
        allSelected() {
            return Object.keys(this.selectedFiltersForSubstitution).length === Object.keys(this.currentCategoryFilters).length;
        },
        selectedFiltersSortedByName() {
            return this.sortFiltersByName(this.selectedFiltersForSubstitution);
        },
        sortedProducts() {
            return this.products.sort((a, b) => {
                const orderingEntries = Object.entries(this.ordering);
                for (let [column, direction] of orderingEntries) {
                    let aValue, bValue;

                    if (column === 'name') {
                        aValue = this.getNameString(a.names) || '';
                        bValue = this.getNameString(b.names) || '';
                    } else {
                        aValue = a[column] || '';
                        bValue = b[column] || '';
                    }

                    if (aValue < bValue) {
                        return direction === 'asc' ? -1 : 1;
                    } else if (aValue > bValue) {
                        return direction === 'asc' ? 1 : -1;
                    }
                }
                return 0;
            });
        }
    },
    beforeUnmount() {
        this.fetchTask.cancel();
        this.substitutionTask.cancel();
        this.updateTask.cancel();
    }
};
</script>
<style scoped>
.product-cell {
    width: 100%;
}
.table-image {
    width: 38px;
    height: 38px;
}
.text-container {
    min-width: 0;
    overflow: hidden;
    flex: 1;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
.text-nowrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
